import { beginFileDialog } from './fileDialogGuard';

/**
 * Save-a-file entry point for every user-facing download in the app.
 *
 * Chrome/Edge get a real "Save As" dialog via the File System Access API so
 * the user picks the location and file name. The dialog only opens inside the
 * click's transient activation, so `saveAs` opens it BEFORE fetching the blob
 * (a slow fetch would otherwise expire the gesture). Everywhere the picker is
 * unavailable (Firefox/Safari) or fails to open, we fall back to the classic
 * hidden-anchor auto-download.
 */

// Minimal File System Access API surface (not in lib.dom for our TS target).
interface FileSystemWritableStream {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}
interface SaveFileHandle {
  createWritable(): Promise<FileSystemWritableStream>;
}
interface SaveFilePickerType {
  description: string;
  accept: Record<string, string[]>;
}
type ShowSaveFilePicker = (options: {
  suggestedName?: string;
  types?: SaveFilePickerType[];
}) => Promise<SaveFileHandle>;

// Extension → MIME for the picker's file-type dropdown. The dialog can't read
// response headers (with picker-first it opens before the request is made), so
// the type is derived from the suggested filename instead.
const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  csv: 'text/csv',
  txt: 'text/plain',
  zip: 'application/zip',
  json: 'application/json',
  xml: 'text/xml',
  // EDI interchanges (X12 210 and the like) download from the invoice EDI preview.
  edi: 'text/plain',
  html: 'text/html',
  htm: 'text/html',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  eml: 'message/rfc822',
};

/** File-type entry for the Save As dialog, or undefined when the extension is
 *  unknown (the dialog then shows "All files" and keeps the name as typed). */
function pickerTypes(suggestedName: string): SaveFilePickerType[] | undefined {
  const ext = /\.([a-z0-9]+)$/i.exec(suggestedName)?.[1]?.toLowerCase();
  const mime = ext ? MIME_BY_EXT[ext] : undefined;
  if (!ext || !mime) return undefined;
  return [{ description: `${ext.toUpperCase()} file`, accept: { [mime]: [`.${ext}`] } }];
}

/**
 * User preference (Settings → Application Preferences → Downloads → Show Save
 * As). Written by WorkingStatusPreferenceContext; read here at download time.
 * Defaults ON — only an explicit 'false' skips the dialog and auto-downloads
 * to the browser's Downloads folder.
 */
export const SHOW_SAVE_AS_DIALOG_KEY = 'showSaveAsDialog';

const showSaveAsDialogEnabled = (): boolean =>
  localStorage.getItem(SHOW_SAVE_AS_DIALOG_KEY) !== 'false';

const isAbort = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';

function anchorDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // The download reads the object URL asynchronously; revoking right after
  // click() can cancel it. House pattern: hold it for 60s, then release.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Prompt for a save location, then fetch the file and write it there.
 * Use this form when the blob comes from the server — the picker opens first
 * so the prompt survives slow fetches. A cancelled picker skips the fetch.
 *
 * Resolves true when the file was saved (or handed to the fallback download),
 * false when the user cancelled the Save As dialog — so callers don't report
 * success on a cancel.
 */
export async function saveAs(suggestedName: string, getBlob: () => Promise<Blob>): Promise<boolean> {
  const showSaveFilePicker = (window as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker;
  if (showSaveFilePicker && showSaveAsDialogEnabled()) {
    let handle: SaveFileHandle | undefined;
    // Native picker dismissal leaks Escape/Cancel into open <dialog> drawers.
    beginFileDialog();
    try {
      const types = pickerTypes(suggestedName);
      handle = await showSaveFilePicker(types ? { suggestedName, types } : { suggestedName });
    } catch (err) {
      if (isAbort(err)) return false; // user cancelled — not an error, no download
      handle = undefined; // picker couldn't open (expired gesture, blocked context)
    }
    if (handle) {
      const blob = await getBlob();
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    }
  }
  anchorDownload(await getBlob(), suggestedName);
  return true;
}

/** Prompt for a save location for a blob already in memory (client-built exports). */
export function saveBlob(blob: Blob, suggestedName: string): Promise<boolean> {
  return saveAs(suggestedName, () => Promise.resolve(blob));
}
